import { z } from "zod";
import { confirmableConfig, confirmableDefaults } from "./confirmable.js";
import { optionalUnlessRequired, type FieldDescriptor } from "./types.js";

// International, defaults to +234, validates NG mobile patterns (docs/01-PRODUCT-SPEC.md §3.2).
const configSchema = z.object({
  ...confirmableConfig,
  defaultCountry: z.string().length(2).default("NG"),
  // When true, only Nigerian mobile numbers are accepted. Off by default: a referee or guardian
  // may legitimately be abroad, and rejecting their number would block the whole application.
  nigerianMobileOnly: z.boolean().default(false),
});

/**
 * NG mobile: +234 then a 10-digit subscriber number beginning 70/71/80/81/90/91, or the local
 * 0-prefixed form. Deliberately permissive about spaces and dashes — the applicant is typing on a
 * phone, and normalisation to E.164 happens on the way into storage, not here.
 */
const NG_MOBILE = /^(?:\+?234|0)(?:70|71|80|81|90|91)\d{8}$/;
const E164_LOOSE = /^\+?[1-9]\d{6,14}$/;

export const phoneField: FieldDescriptor<z.infer<typeof configSchema>> = {
  type: "phone",
  label: "Phone number",
  category: "basic_info",
  icon: "Phone",
  configSchema,
  valueSchema: (config, isRequired) => {
    const base = z
      .string()
      .trim()
      .transform((value) => value.replace(/[\s()-]/g, ""))
      .refine(
        (value) => (config.nigerianMobileOnly ? NG_MOBILE.test(value) : E164_LOOSE.test(value)),
        config.nigerianMobileOnly
          ? "Enter a valid Nigerian mobile number"
          : "Enter a valid phone number",
      );
    return optionalUnlessRequired(base, isRequired);
  },
  defaultConfig: { ...confirmableDefaults, defaultCountry: "NG", nigerianMobileOnly: false },
  supportsProfileBinding: true,
  defaultSearchable: true,
  renderHints: {},
};
